import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Plus } from '@hpi-sox/hpstellar-icons';

import addCTOToCart from './add-to-cart';
import usePageComponents from '../../../hooks/usePageComponents';
import useTemplateTheme from '../../../hooks/useTemplateTheme';
import useDeviceInfo from '../../../hooks/useDeviceInfo';
import useCartState from '../../../hooks/useCartState';
import { useTriggerXsellModal } from '../../../hooks/useAddToCart';
import { useMSRPProps, useThresholdDisabled, getCustomSaleText } from '../../../hooks/useProductPrice';
import useSEO from '../../../hooks/useSEO';
import { getSelectedRecommendedConfig } from '../../util';
import { addGtmAddToCart, formatMetricValue } from '../../../metrics/metrics-helpers';
import usePageLoader from '../../../hooks/usePageLoader';
import { getCTOConfigDetails } from '../product-identity-details';
import { useClientSideABTest } from '../../../abtest/components/abtest';

const EMPTY_OBJ = {};

const handleAddToCart =
    (
        baseConfig,
        shipDate,
        history,
        trackCustomMetric,
        ctoProductInitial,
        price,
        recommendedConfigs,
        items,
        setPageLoader,
        seo,
        onAdd,
    ) =>
    () => {
        setPageLoader({ loading: true });
        const selectedRecommendedConfig = getSelectedRecommendedConfig(recommendedConfigs);
        const configDetails = getCTOConfigDetails(baseConfig, ctoProductInitial, price, seo);
        addCTOToCart(
            baseConfig,
            shipDate,
            history,
            trackCustomMetric,
            ctoProductInitial,
            price,
            selectedRecommendedConfig,
            items,
            configDetails,
            onAdd,
        )
            .then(() => {
                setTimeout(() => {
                    setPageLoader({ loading: false });
                }, 1000);
            })
            .catch(e => {
                setPageLoader({ loading: false });
            });
    };

export default (trackCustomMetric, options = { endIcon: true }) => {
    const { cartId, cartInfo } = useCartState();
    const { items } = cartInfo || {};
    const { device } = useDeviceInfo(['device']);
    const [loader, setLoader] = usePageLoader();
    const { baseConfig, shipDate, ctoPrice, errors, ctoProductInitial } = usePageComponents([
        'baseConfig',
        'shipDate',
        'ctoPrice',
        'errors',
        'ctoProductInitial',
    ]);
    const { css, button, price: themePriceProps = {} } = useTemplateTheme(['button', 'price']);
    const { ctoXsellType } = useClientSideABTest('ctoXsellType');
    const triggerXsellModal = useTriggerXsellModal();
    const { ctaText, sku, recommendedConfigs, initialPrice } = baseConfig || EMPTY_OBJ;
    const msrp = useMSRPProps({ position: 'bottom-start' });
    const { color } = button || {};
    const priceValue = typeof ctoPrice === 'undefined' && initialPrice ? initialPrice : ctoPrice;
    const isDisabled = useThresholdDisabled({ ...priceValue, sku });
    const disableAddToCart = Object.keys(errors || {}).length > 0 || isDisabled;
    const history = useHistory();
    //initial price is provided for crawlers, use it when available until async price data becomes available
    const price = typeof ctoPrice === 'undefined' && initialPrice ? initialPrice : ctoPrice;
    const customSaleText = getCustomSaleText(price);
    const forInlineAttribute = device !== 'mobile';
    const recommendedConfig =
        baseConfig &&
        baseConfig.recommendedConfigs &&
        Object.values(baseConfig.recommendedConfigs).find(config => config && config.selected === 'yes');

    const configurationMetric =
        recommendedConfig && recommendedConfig.configName && formatMetricValue(recommendedConfig.configName);

    const gtmAddToCart = addGtmAddToCart(
        ctoProductInitial,
        { list: 'cto', forInlineAttribute, configurationMetric, isMainSku: true },
        { [sku]: price },
        cartId,
    );
    const setPageLoader = useCallback(
        value => {
            if (value && !loader.loading) {
                setLoader(value);
            } else if (!value && loader.loading) {
                setLoader(value);
            }
        },
        [loader, setLoader],
    );
    const seo = useSEO();
    return {
        buttonProps: {
            endIcon: options?.endIcon ? <Plus size="s" /> : null,
            onClick: handleAddToCart(
                baseConfig,
                shipDate,
                history,
                trackCustomMetric,
                ctoProductInitial,
                price,
                recommendedConfigs,
                items,
                setPageLoader,
                seo,
                ctoXsellType === 'modal'
                    ? resp => {
                          triggerXsellModal(ctoProductInitial, resp);
                      }
                    : null,
            ),
            disabled: disableAddToCart,
            ...(gtmAddToCart || {}),
            ...(configurationMetric ? { 'data-gtm-configuration': configurationMetric } : {}),
            'data-gtm-object': 'mainSKU',
            color: color,
            children: ctaText,
        },
        price: { ...price, msrp },
        customSaleText,
        priceBlockClassNameL: css && `${css}-price-block`,
        isDarkCto: !!css,
        themePriceProps,
    };
};
